import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    Form,
    Button,
    Typography,
    message,
    Row,
    Col,
    Space,
    Tag,
    Avatar,
    Skeleton,
    Layout,
    Grid,
} from 'antd';
import { MailOutlined, LockOutlined, EditOutlined } from '@ant-design/icons';
import { getUser, sendVerificationEmail } from './ProfileServices';
import { getStoredUser } from '../../Config/ApiConfig';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import ChangePassword from './ChangePassword/ChangePassword';
import EditProfile from './EditProfile/EditProfile';
import { format } from 'date-fns';

const { Text, Title } = Typography;

const UserProfile = () => {
    const { t } = useTranslation();
    const { id: userId } = getStoredUser();
    const { handleError } = useErrorHandler();
    const { setLoading, changeUser, user } = useGlobal();
    const [isSkeleton, setIsSkeleton] = useState(false);
    const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
    const [openEditProfile, setOpenEditProfile] = useState(false);
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();

    const handleChangePassword = () => {
        setOpenChangePasswordModal(true);
    };

    const fetchUserData = async () => {
        try {
            setIsSkeleton(true);
            setLoading(true);
            const { data } = await getUser(userId);
            changeUser(data);
            form.setFieldsValue({
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
            });
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
            setIsSkeleton(false);
        }
    };

    const initForm = async () => {
        form.setFieldsValue({
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
        });
    };

    useEffect(() => {
        initForm();
    }, [user]);
    const handleVerifyEmail = async () => {
        setVerificationLoading(true);
        try {
            await sendVerificationEmail(userId);
            message.success(t('verification_email_sent'));
        } catch (error) {
            handleError(error);
        } finally {
            setVerificationLoading(false);
        }
    };

    const renderVerificationStatus = () => {
        if (!user?.email) {
            return <Tag color="error">{t('no_email_added')}</Tag>;
        }

        if (user?.is_verified) {
            return <Tag color="success">{t('verified_account')}</Tag>;
        }

        return (
            <Space>
                <Tag color="warning">{t('pending_verification')}</Tag>
                <Button
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={handleVerifyEmail}
                    loading={verificationLoading}
                >
                    {t('verify_email')}
                </Button>
            </Space>
        );
    };

    const handleCloseEditProfile = () => {
        setOpenEditProfile(false);
    }

    const handleOpenEditProfile = () => {
        setOpenEditProfile(true);
    }

    const handleCloseChangePassword = () => {
        setOpenChangePasswordModal(false);
    }

    const renderSkeletonProfile = () => (
        <Layout>
            <Card style={{ marginBottom: 16 }}>
                <Row align="middle" gutter={[16, 16]}>
                    <Col>
                        <Skeleton.Avatar active size={64} />
                    </Col>
                    <Col flex="1">
                        <Skeleton.Input active size="small" style={{ width: 200 }} />
                        <Skeleton.Input active size="small" style={{ width: 150, marginTop: 8 }} />
                    </Col>
                    <Col style={{ textAlign: 'right' }}>
                        <Skeleton.Button active size="small" style={{ width: 100 }} />
                    </Col>
                </Row>
            </Card>
            <Row gutter={[8, 8]}>
                <Col xs={24} lg={16}>
                    <Card>
                        <Skeleton active />
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card>
                        <Skeleton active />
                    </Card>
                </Col>
            </Row>
        </Layout>
    );

    return (
        <Layout>
            {openEditProfile && (
                <EditProfile handleClose={handleCloseEditProfile} user={user} fetchUser={fetchUserData} locale={user?.locale} timezone={user?.timezone} />
            )}
            {openChangePasswordModal && <ChangePassword handleClose={handleCloseChangePassword} />}

            {isSkeleton ? renderSkeletonProfile() : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Card bordered={false}>
                        <Row align="middle" gutter={[16, 16]}>
                            <Col>
                                <Avatar size={64} src={user?.picture_url}>
                                    {!user?.avatar && `${user?.first_name[0]}${user?.last_name[0]}`}
                                </Avatar>
                            </Col>
                            <Col>
                                <Title level={4} style={{ margin: 0 }}>
                                    {user?.first_name} {user?.last_name}
                                </Title>
                                <Text type="secondary">
                                    {user?.email || t('no_email_added')}
                                </Text>
                                {user?.discount_percentage ? (
                                    <div style={{ marginTop: '4px' }}>
                                        <Tag color="green" style={{ fontSize: '14px', padding: '2px 8px' }}>
                                            {t('discount')}: {user?.discount_percentage}%
                                        </Tag>
                                        <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                            {t('expires')}: {user?.discountExpiry
                                                ? format(new Date(user?.discount_expiry), "yyyy-MM-dd")
                                                : t('not_available')}
                                        </Text>
                                    </div>
                                ) : null}
                            </Col>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                                {renderVerificationStatus()}
                            </Col>
                        </Row>
                    </Card>
                    <Card
                        style={{
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        }}
                        title={
                            <Row justify="space-between" align="middle">
                                <Title level={5} style={{ margin: 0 }}>
                                    {t('personal_information')}
                                </Title>
                                {!screens.xs && (
                                    <Space>
                                        <Button
                                            icon={<LockOutlined />}
                                            onClick={handleChangePassword}
                                        >
                                            {t('change_password')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            onClick={handleOpenEditProfile}
                                        >
                                            {t('edit_profile')}
                                        </Button>
                                    </Space>
                                )}
                                {screens.xs && (
                                    <Space>
                                        <Button shape="circle" icon={<LockOutlined />}
                                            onClick={handleChangePassword}
                                        />
                                        <Button type="primary" shape="circle" icon={<EditOutlined />} onClick={handleOpenEditProfile}
                                        />
                                    </Space>
                                )}
                            </Row>
                        }
                    >
                        <Row gutter={[32, 32]}>
                            <Col xs={24} sm={12}>
                                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">{t('first_name')}</Text>
                                        <Title level={5} style={{ margin: 0 }}>
                                            {user?.first_name}
                                        </Title>
                                    </Space>
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">{t('last_name')}</Text>
                                        <Title level={5} style={{ margin: 0 }}>
                                            {user?.last_name}
                                        </Title>
                                    </Space>
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">{t('gender')}</Text>
                                        <Title level={5} style={{ margin: 0 }}>
                                            {user?.gender || t('not_available')}
                                        </Title>
                                    </Space>
                                </Space>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">{t('email_address')}</Text>
                                        <Title level={5} style={{ margin: 0 }}>
                                            {user?.email || t('no_email_added')}
                                        </Title>
                                    </Space>
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">{t('member_since')}</Text>
                                        <Title level={5} style={{ margin: 0 }}>
                                            {user?.created_at ? format(user?.created_at, "yyyy-MM-dd HH:mm") : t('not_available')}
                                        </Title>
                                    </Space>
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">{t('locale')}</Text>
                                        <Title level={5} style={{ margin: 0 }}>
                                            {user?.locale || t('not_available')}
                                        </Title>
                                    </Space>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                    <Row gutter={[16, 16]} align="stretch">
                        {/* Discount card has been moved to the profile header */}
                    </Row>
                </Space>
            )}
        </Layout>
    );
};

export default UserProfile;
